import type { CSSProperties } from 'react'
import { tv } from 'tailwind-variants'

import { colors, gradients } from '@gto/ui/theme/design-tokens'
import { typography } from '@gto/ui/typography'

/**
 * Density is component-owned — TanStack has no notion of it. This map is the SINGLE
 * source of the row-height px: `TableRow`/`TableHeader` apply it as inline
 * `style.height`, and `use-data-table.ts` feeds the SAME value into the virtualizer's
 * `estimateSize`, so a windowed row's height can never disagree with its spacer (no
 * parallel `h-[Npx]` class to hand-sync). Values are the product row-density scale.
 */
export type DataTableDensity = 'sub-compact' | 'compact' | 'standard' | 'comfortable'

export const DATA_TABLE_ROW_HEIGHT: Record<DataTableDensity, number> = {
  // ~20px ultra-dense body row for the nodelock combos / hand-classes editing grids
  // (FE-789). Feeds the virtualizer `estimateSize` and the inline row height in lockstep,
  // like every other density.
  'sub-compact': 20,
  compact: 30,
  standard: 40,
  comfortable: 50,
}

/**
 * 1px gap between rounded rows. Part of the virtualizer's per-row slot
 * (`estimateSize = row height + gap`), NOT an outer margin on the windowed rows — a
 * margin wouldn't be reflected in the virtualizer's `translateY` and would drift the
 * spacer. The non-windowed path realises the same gap via the row variant's `mb-[1px]`.
 */
export const DATA_TABLE_ROW_GAP_PX = 1

/**
 * Header row height — its OWN density axis, decoupled from the body row height: MUI
 * scales the header from a 56px base, so it is TALLER than body rows, and a header
 * sized to the row height reads visibly short next to the reference grid. Single
 * source, applied by `TableHeader` as inline `style.height`. Only the BODY row height
 * feeds the virtualizer's `estimateSize`.
 */
export const DATA_TABLE_HEADER_HEIGHT: Record<DataTableDensity, number> = {
  // `sub-compact` reuses compact's 39px: headerless consumers (hand-classes) never show
  // it, and combos accepts 39 (down from MUI's 56 desktop / 38 mobile ≈ 39).
  'sub-compact': 39,
  compact: 39,
  standard: 56,
  comfortable: 72,
}

/** Vertical breathing room between a table header and its first body row. */
export const DATA_TABLE_HEADER_BODY_GAP_PX = 8

export const DEFAULT_DATA_TABLE_DENSITY: DataTableDensity = 'standard'

/**
 * Paper-elevation level a table surface (the card body, or the header band) paints. A
 * consumer sets the card via `surface` so the table matches the panel it sits in
 * (`surface="elevation3"` → the card is invisible against an elevation3 panel, and the
 * body reads as part of it), and the header band independently via `headerSurface`. The two
 * are INDEPENDENT rather than one derived from the other: the only relationship worth
 * enforcing is "the header is visibly distinct from its card", and no arithmetic on a
 * 5-step scale satisfies that at both ends without inverting the light-above-dark language
 * somewhere. The defaults (`elevation2` card / `elevation3` header) are the original
 * surfaces, so a consumer that sets neither is unchanged.
 */
export type DataTableSurface =
  | 'elevation1'
  | 'elevation2'
  | 'elevation3'
  | 'elevation4'
  | 'elevation5'

export const DEFAULT_DATA_TABLE_SURFACE: DataTableSurface = 'elevation2'

/** The header band's elevation when `headerSurface` is not set — one step above the default card. */
export const DEFAULT_DATA_TABLE_HEADER_SURFACE: DataTableSurface = 'elevation3'

/**
 * The opaque `bg-paper-elevation*` class each surface paints. ONE source for every element
 * that must match a table surface — the card, the header band, and the header cell's
 * affordance chip (which sits ON the header and would otherwise show a mismatched seam when
 * the header is not the default elevation). Literal strings so Tailwind's JIT keeps them.
 */
export const SURFACE_BG: Record<DataTableSurface, string> = {
  elevation1: 'bg-paper-elevation1',
  elevation2: 'bg-paper-elevation2',
  elevation3: 'bg-paper-elevation3',
  elevation4: 'bg-paper-elevation4',
  elevation5: 'bg-paper-elevation5',
}

/**
 * The RAW colour each surface paints, the `SURFACE_BG` twin for the places a Tailwind class
 * can't reach — today the scroll root's `background-image` below, which needs a literal colour.
 * Same single source (`colors.paper`) the preset feeds `bg-paper-elevation*` from, so the class
 * and the colour can't drift.
 */
export const SURFACE_COLOR: Record<DataTableSurface, string> = {
  elevation1: colors.paper.elevation1,
  elevation2: colors.paper.elevation2,
  elevation3: colors.paper.elevation3,
  elevation4: colors.paper.elevation4,
  elevation5: colors.paper.elevation5,
}

/**
 * One sticky chrome band to continue across the gutter: the header at the top, the summary row
 * at the bottom. A TOP-placed summary joins the header's band instead (see `DataTable`).
 */
export type StickyBand = { surface: DataTableSurface; heightPx: number }

/**
 * Continues each STICKY BAND's colour across the VERTICAL SCROLLBAR'S GUTTER, as the scroll
 * root's own background — the header band at the top edge, the summary band at the bottom.
 *
 * The defect without it: `::-webkit-scrollbar-track` is transparent, so the 6px gutter shows
 * whatever is behind it — the CARD `surface`. Beside the body that is invisible (the body IS
 * the card surface), but beside a sticky BAND it is a 6px notch in the wrong colour the
 * moment the thumb scrolls away from that edge, and the stronger the band ≠ `surface`
 * contrast the louder it reads.
 *
 * Two non-fixes, for the next person who tries them:
 * - **Widen the band.** It is `w-max min-w-full`, and inside a scroll container `min-w-full`
 *   resolves against the CONTENT box, which excludes the gutter. No child of the scroller can
 *   paint there at all — content is clipped to the content box — so no width fixes this.
 * - **Overlay the card.** An absolutely-positioned patch on the (non-scrolling) card paints
 *   ABOVE the scroll root, so it would cover the thumb's end and render it visibly truncated
 *   whenever the list is scrolled near that edge.
 *
 * The scroll root's own background is the only layer that sits BEHIND the transparent track.
 * `background-attachment` stays at its default `scroll`, which anchors the background to the
 * PADDING box (the gutter is inside it) and holds it still while the content scrolls — the same
 * frame the sticky bands occupy, so each band and its gutter strip stay aligned under vertical
 * AND horizontal scroll, and `background-position: bottom` lands on the scrollport's bottom edge
 * (where the summary sticks), not on the scrolled content's end. Spanning the full width also
 * makes it correct under RTL, where the gutter moves to the left edge.
 *
 * A band with height `0` is one that does not render (a HEADERLESS grid, or a table with no
 * summary row): its layer is dropped, and with no layers left this returns `undefined` so the
 * scroll root keeps the card's surface. Resolving that here rather than branching at the call
 * site is what keeps `hideHeader` / `summaryRow` from growing another conditional.
 */
export const stickyBandBackgroundStyle = (bands: {
  header?: StickyBand
  summary?: StickyBand
}):
  | Pick<
      CSSProperties,
      'backgroundImage' | 'backgroundPosition' | 'backgroundRepeat' | 'backgroundSize'
    >
  | undefined => {
  const layers = [
    { band: bands.header, position: 'top' },
    { band: bands.summary, position: 'bottom' },
  ].flatMap(({ band, position }) =>
    band === undefined || band.heightPx <= 0 ? [] : [{ ...band, position }],
  )

  if (layers.length === 0) {
    return undefined
  }

  return {
    // A two-stop gradient of ONE colour is the only way to get a sized solid fill:
    // `background-color` always covers the whole padding box and would repaint the body region.
    backgroundImage: layers
      .map(({ surface }) => {
        const color = SURFACE_COLOR[surface]

        return `linear-gradient(${color}, ${color})`
      })
      .join(', '),
    backgroundPosition: layers.map(({ position }) => position).join(', '),
    backgroundRepeat: layers.map(() => 'no-repeat').join(', '),
    backgroundSize: layers.map(({ heightPx }) => `100% ${heightPx}px`).join(', '),
  }
}

/**
 * The bottom overflow fade keyed by the CARD surface: each `overflow-fade-*` token fades from
 * that paper elevation (opaque, bottom edge) up to the SAME colour at zero alpha, so the last
 * rows dissolve into whatever background the card paints.
 *
 * These are NAMED `backgroundImage` tokens from the Tailwind preset, NOT `bg-gradient-to-t
 * from-*` utilities: the DS preset defines `theme.backgroundImage` OUTSIDE `extend`, which
 * REPLACES Tailwind's stock values — so the built-in directional `gradient-to-*` utilities do
 * not exist here and `bg-gradient-to-t` silently renders nothing (background-image: none).
 * Lives in this non-component module so `BottomOverflowShadow` exports only its component.
 */
export const bottomShadowGradientBySurface: Record<DataTableSurface, string> = {
  elevation1: 'bg-overflow-fade-elevation1',
  elevation2: 'bg-overflow-fade-elevation2',
  elevation3: 'bg-overflow-fade-elevation3',
  elevation4: 'bg-overflow-fade-elevation4',
  elevation5: 'bg-overflow-fade-elevation5',
}

export const endFadeBackgroundStyle = (bands: {
  body: DataTableSurface
  header?: StickyBand
  summary?: StickyBand
}): Pick<
  CSSProperties,
  'backgroundImage' | 'backgroundPosition' | 'backgroundRepeat' | 'backgroundSize'
> => {
  const headerPx = Math.max(bands.header?.heightPx ?? 0, 0)
  const summaryPx = Math.max(bands.summary?.heightPx ?? 0, 0)

  const layers = [
    ...[
      { band: bands.header, edge: 'top' },
      { band: bands.summary, edge: 'bottom' },
    ].flatMap(({ band, edge }) =>
      band === undefined || band.heightPx <= 0
        ? []
        : [
            {
              surface: band.surface,
              position: `right ${edge}`,
              size: `100% ${band.heightPx}px`,
            },
          ],
    ),
    {
      surface: bands.body,
      position: headerPx === 0 ? 'right top' : `right 0px top ${headerPx}px`,
      size:
        headerPx + summaryPx === 0 ? '100% 100%' : `100% calc(100% - ${headerPx + summaryPx}px)`,
    },
  ]

  return {
    backgroundImage: layers
      .map(({ surface }) => gradients.overlay.endFade(SURFACE_COLOR[surface]))
      .join(', '),
    backgroundPosition: layers.map(({ position }) => position).join(', '),
    backgroundRepeat: layers.map(() => 'no-repeat').join(', '),
    backgroundSize: layers.map(({ size }) => size).join(', '),
  }
}

/**
 * The row-separator model, ONE axis with four states rather than a boolean plus an
 * inset flag: an inset separator is meaningless without a separator, so the pair would
 * make `{ divider: false, inset: true }` constructible and unrenderable.
 * - `false` — floating rounded rows with a 1px gap (default, MUI parity).
 * - `true` — contiguous rows with a full-bleed 1px bottom border (DataGridPro parity).
 * - `'inset'` — contiguous rows with the line inset 16px, drawn at the row top.
 * - `'ruled'` — `true`'s row model in the dimmer opaque `table-rule` hairline, extended to
 *   the header band and the summary band so every horizontal edge of the table matches.
 *   The wide-table model: 6+ columns. Rules ROWS ONLY — there are deliberately no column
 *   rules (see `dataTableRowVariants`).
 * See `dataTableRowVariants` for the full rationale of each.
 */
export type DataTableDivider = boolean | 'inset' | 'ruled'

/**
 * The border colour a table of each row model draws its full-bleed horizontal edges in — the
 * `SURFACE_BG` of lines. ONE source for every edge that has to match the rows: the header band,
 * a bordered row, the summary band and its leading overlay, an expanded detail panel's closing
 * line, and the load-more error strip. Each of those composes its own EDGE (`border-b`,
 * `border-t`, or the one `placement` picks), so the colour is the only part they can share.
 *
 * Keyed like a `tv` variant, so a preset takes it whole (`divider: RULE_COLOR`). An `'inset'`
 * ROW draws its own `::after` hairline and never reads this; that entry is the colour of the
 * chrome edges on an inset table.
 */
const RULE_COLOR = {
  // The MUI divider, white at 12%.
  false: 'border-divider',
  true: 'border-divider',
  inset: 'border-divider',
  // The opaque `table-rule` hairline, quiet enough to repeat down a wide table.
  ruled: 'border-table-rule',
} as const satisfies Record<`${DataTableDivider}`, string>

/**
 * The surfaces `'ruled'` is legible on.
 *
 * `table-rule` is OPAQUE, so unlike the translucent `divider` it holds its COLOUR constant
 * across surfaces rather than its CONTRAST — and the scale catches up with it. Measured
 * against the shipped palette (WCAG ratio, `#1F1F21` vs each `paper-elevation*`):
 *
 * | surface      | ratio | reads as                                  |
 * | ------------ | ----- | ----------------------------------------- |
 * | `elevation1` | 1.137 | the strongest the hairline gets            |
 * | `elevation2` | 1.098 | the default card — the reference rule      |
 * | `elevation3` | 1.045 | the default band — quieter but present     |
 * | `elevation4` | 1.011 | effectively INVISIBLE                      |
 * | `elevation5` | 1.092 | INVERTED — the rule is darker than its band |
 *
 * The last two are the failure modes worth a name: at `elevation4` the rule disappears into
 * its own background, and at `elevation5` it reads as a groove rather than a line, because the
 * light-above-dark language flips. Neither mis-renders — the class is still correct and jsdom
 * still sees it — so `useDataTablePropValidation` dev-warns rather than throwing, and the type
 * stays wide: `surface` means the same thing on all four row models and narrowing it under one
 * of them would make the prop's meaning depend on a sibling prop.
 */
export const RULE_LEGIBLE_SURFACES: ReadonlySet<DataTableSurface> = new Set([
  'elevation1',
  'elevation2',
  'elevation3',
])

/** Column alignment shared by header + body cells. */
export type DataTableAlign = 'left' | 'center' | 'right'

/**
 * Corner radius of the card. `lg` (16px) is the DS card radius; `sm` (6px) matches a floating
 * ROW's own radius.
 *
 * It is an axis because the card CLIPS its rows: a floating row (`divider: false`) whose top
 * or bottom edge is flush with the card's has THAT corner carved by the card's 16px arc while
 * its other two keep the row's own 6px `rounded-sm`, so a hovered row renders visibly lopsided
 * against its neighbours. At `sm` the two arcs coincide and every row's corners render alike.
 *
 * NOT a consumer prop — `resolveCardRadius` derives it, because whether a row is flush is a
 * fact about the table's own configuration, not a taste the caller should have to notice.
 * `embedded` is the deliberate exception: the enclosing panel owns the silhouette, so it
 * short-circuits this derivation with `none`.
 */
export type DataTableCardRadius = 'lg' | 'sm' | 'none'

/**
 * The card radius for a given row model + bounding, per `DataTableCardRadius`. A floating row
 * ends up flush with the card when either edge has nothing between it and the card:
 * - **top** — `hideHeader`, so the first row starts at the scroll top with no band above it.
 * - **bottom** — no `height`, so the card hugs its content and ends at the last row. (With a
 *   bounded height the scroll root fills the card, and a short list leaves card surface below
 *   the last row instead.)
 *
 * Any divider row model (`true`, `'inset'` or `'ruled'`) is square and contiguous, so the card's
 * own 16px arc is always the right silhouette for them. An embedded table bypasses this helper
 * because its enclosing panel supplies the radius and clipping.
 */
export const resolveCardRadius = ({
  rowDivider,
  hideHeader,
  hasBoundedHeight,
}: {
  rowDivider: DataTableDivider
  hideHeader: boolean
  hasBoundedHeight: boolean
}): Exclude<DataTableCardRadius, 'none'> =>
  rowDivider === false && (hideHeader || !hasBoundedHeight) ? 'sm' : 'lg'

/**
 * The two chrome bands' elevations, when the consumer has not named one. Resolved SEPARATELY
 * because `'ruled'` moves only the header:
 *
 * - The HEADER blends into the card. A ruled table separates with LINES, so a header on its own
 *   lighter surface separates the same edge twice — and the rule reads weaker there, since the
 *   `table-rule` hairline is opaque and keeps its colour, not its contrast, across surfaces
 *   (measured 1.045 against the elevation3 band vs 1.098 against the elevation2 body).
 * - The SUMMARY BAND stays LIFTED. It is not a column header; it is an aggregate pinned over the
 *   rows and has to stay legible as they scroll under it. Blended, it reads as one more data row.
 *
 * Every other model keeps the elevation3 band on both, so no existing table moves. An explicit
 * `headerSurface` still wins on BOTH — it is the chrome-band knob, and a consumer that names one
 * is asking for a matched pair. That is why `DataTable` does not default the prop at its
 * destructure: a default cannot be told apart from an explicit value.
 */
export const resolveChromeBandSurfaces = ({
  headerSurface,
  rowDivider,
  surface,
}: {
  headerSurface: DataTableSurface | undefined
  rowDivider: DataTableDivider
  surface: DataTableSurface
}): { header: DataTableSurface; summary: DataTableSurface } => ({
  header: headerSurface ?? (rowDivider === 'ruled' ? surface : DEFAULT_DATA_TABLE_HEADER_SURFACE),
  summary: headerSurface ?? DEFAULT_DATA_TABLE_HEADER_SURFACE,
})

/**
 * The gap between the header band and the first body row, defaulted per row model — the second
 * chrome default `'ruled'` moves, beside `resolveChromeBandSurfaces` for the first. `'sm'` under
 * a lifted header, `'none'` under `'ruled'`, where the header's rule is the first line of the
 * body's rail. An explicit value wins. `DataTableProps.headerBodySpacing` has the reasoning.
 */
export const resolveHeaderBodySpacing = (
  headerBodySpacing: 'none' | 'sm' | undefined,
  rowDivider: DataTableDivider,
): 'none' | 'sm' => headerBodySpacing ?? (rowDivider === 'ruled' ? 'none' : 'sm')

/**
 * An expanded row's DETAIL PANEL. The panel closes the table from BELOW its master row — the one
 * horizontal edge that row cannot draw for itself — so it takes the table's rule colour.
 *
 * `last` is the row model's trailing exception, landed here: an expanded LAST row makes the
 * panel the body's bottom edge, and `DataTableBodyRow` deliberately keeps the master row's own
 * rule (suppressing there would erase the line between a row and its panel). `'ruled'` ONLY. The
 * doubled bottom edge is real on `true` too and stays: fixing it there restyles the two shipped
 * `rowDivider` + `detailPanel` tables (admin-web's insider feature-records panel, the fairplay
 * results shell), which is a change of its own.
 */
export const dataTableDetailPanelVariants = tv({
  base: 'w-full border-b bg-paper-elevation3',
  variants: {
    divider: RULE_COLOR,
    last: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [{ divider: 'ruled', last: true, class: 'border-b-0' }],
  defaultVariants: {
    divider: false,
    last: false,
  },
})

/**
 * The load-more error strip. It renders INSIDE the scroll flow directly under the last row, so
 * its top line reads as one more rule in the body and takes the table's rule colour.
 */
export const dataTableLoadMoreErrorVariants = tv({
  base: 'flex items-center justify-center gap-3 border-t px-4 py-3 text-center',
  variants: {
    divider: RULE_COLOR,
  },
  defaultVariants: {
    divider: false,
  },
})

/**
 * The CARD that wraps BOTH the `role="grid"` scroll container and the body-replacing
 * state overlay: a rounded, clipped, `flex flex-col` surface (see the render-side comment
 * in `data-table.tsx`). `surface` sets its background so the table can match the panel it
 * sits in. Default `elevation2` reproduces the background this element carried inline before
 * the prop existed. `radius` is the clip radius, supplied by `resolveCardRadius` unless
 * `embedded` delegates the silhouette to its enclosing panel — see `DataTableCardRadius`.
 */
export const dataTableCardVariants = tv({
  base: 'relative flex w-full flex-col overflow-hidden',
  variants: {
    surface: SURFACE_BG,
    radius: {
      lg: 'rounded-lg',
      sm: 'rounded-sm',
      none: 'rounded-none',
    },
  },
  defaultVariants: {
    surface: DEFAULT_DATA_TABLE_SURFACE,
    radius: 'lg',
  },
})

/**
 * Scroll container / virtualization root. `isolate` opens the stacking context the
 * sticky header + pinned columns layer within; `overflow-auto` makes this the scroll
 * element the virtualizer measures. Thin custom scrollbars (6px both axes, MUI parity) via
 * `::-webkit-scrollbar` variants; the `--clr-black-3` thumb var resolves only when the
 * consuming app imports `@gto/styles`, else the scrollbar degrades to the default. NB: never
 * apply a CSS `mask`/`mask-image` to THIS element to fade an edge — a mask on a scroll
 * container hides its scrollbars in Chromium (why the old bottom scroll-fade mask was dropped);
 * fade via an overlay on the non-scrolling card instead (`BottomOverflowShadow`, the vertical
 * twin of `PinnedColumnShadows`).
 *
 * `group/grid` names this container as the group the active-cell RING gates on: the
 * ring is `group-focus-visible/grid:…`, so it paints only while THIS container matches
 * `:focus-visible` — keyboard focus, not a pointer click. The container is the single
 * `tabIndex=0` tab-stop, so clicking a non-focusable descendant moves DOM focus here;
 * without the gate that pointer-induced focus painted the keyboard ring on the first
 * cell after a mouse sort.
 */
export const dataTableRootVariants = tv({
  base: [
    'group/grid relative isolate w-full overflow-auto',
    '[&::-webkit-scrollbar]:size-[6px]',
    '[&::-webkit-scrollbar-track]:bg-transparent',
    '[&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb]:bg-[var(--clr-black-3)]',
  ],
  variants: {
    /**
     * Clip horizontal overflow instead of scrolling it (MUI `virtualScroller.overflowX: hidden`
     * parity). Rows are `w-max min-w-full`, so a table whose columns sum wider than a narrow
     * container grows a horizontal scrollbar; a consumer with fixed narrow columns in a
     * responsive panel (the nodelock hand-classes lists at a 360px column sum, inside a
     * phone-width drawer) wants those columns clipped, not scrollable. Vertical scrolling —
     * which the virtualizer measures — is untouched: `overflow-auto` still sets the y axis.
     */
    clipHorizontalOverflow: {
      true: 'overflow-x-hidden',
      false: '',
    },
  },
  defaultVariants: {
    clipHorizontalOverflow: false,
  },
})

/**
 * Sticky header row. `w-max min-w-full`, NOT `w-full`: when columns sum wider than the
 * viewport the header must grow to the full column span, so its elevation-3 surface
 * reaches every column and scrolls in lockstep with the body, while still filling the
 * viewport when columns are narrower (`w-full` clamped it and left the right-most
 * columns unpainted). Height comes from `DATA_TABLE_HEADER_HEIGHT` via inline
 * `style.height` (no `h-[Npx]` class to sync). `z-[6]` (= `PINNED_Z_INDEX.header`)
 * out-ranks the ENTIRE body band (unpinned 0 / pinned 2 / focused 4) AND the sticky
 * summary band (5) so the header occludes anything scrolling up under it; being
 * positioned + z-indexed it opens its own stacking context, so its pinned header cells
 * (`z-7`, inline) ride above its unpinned cells.
 */
export const dataTableHeaderVariants = tv({
  base: 'sticky top-0 z-[6] flex w-max min-w-full',
  variants: {
    // The header's OWN elevation, painted directly via the shared `SURFACE_BG` map and
    // independent of the card's — see `DataTableSurface`. Default: the original elevation3.
    surface: SURFACE_BG,
    /**
     * The table's row-separator model, so the band can close its bottom edge with the same
     * line the body draws. ONLY `'ruled'` draws one — the band has never painted a bottom
     * border, and `true` / `'inset'` keep it that way so no existing consumer moves. (It is
     * also why `'inset'` draws its line at the row TOP: without a band border, a
     * bottom-drawn separator leaves the first row's top edge bare.)
     */
    divider: {
      false: '',
      true: '',
      inset: '',
      ruled: `border-b ${RULE_COLOR.ruled}`,
    },
  },
  defaultVariants: {
    surface: DEFAULT_DATA_TABLE_HEADER_SURFACE,
    divider: false,
  },
})

export const dataTableHeaderCellVariants = tv({
  // MUI parity: the shared `body3` token in the `#9E9E9E` header colour (`table.header`).
  base: [typography.body3, 'flex min-w-0 shrink-0 items-center px-3 text-table-header'],
  variants: {
    align: {
      left: 'justify-start text-left',
      center: 'justify-center text-center',
      right: 'justify-end text-right',
    },
  },
  defaultVariants: {
    align: 'left',
  },
})

/**
 * Body wrapper. When windowing, the consumer sets an inline height to the virtualizer's
 * `getTotalSize()` so this is the full-height spacer the absolutely-positioned rows
 * translate within; unwindowed, it is a plain column.
 */
export const dataTableBodyVariants = tv({
  base: 'relative w-full',
})

/**
 * Body row. Four mutually exclusive row models via the `divider` variant:
 * - `divider:false` (default, MUI parity) — a rounded FLOATING row: `rounded-sm` fill,
 *   transparent resting background, 1px inter-row gap via `mb-[1px]` (a no-op on the
 *   absolutely-positioned windowed rows, where the gap comes from the virtualizer slot).
 * - `divider:true` (DataGridPro parity) — a full-width 1px bottom border, rows CONTIGUOUS
 *   (no gap: the preset drops the windowed slot's `DATA_TABLE_ROW_GAP_PX` to 0 in lockstep,
 *   see `data-table.tsx`), square corners, no trailing line on the last row.
 *
 * The trailing exception is keyed off the `last` axis, NOT `:last-child`. Under windowing
 * `:last-child` matches the bottom-most currently MOUNTED row, so the exception travelled with
 * the scroll window: mid-scroll the bottom row of the mounted slice dropped its separator (or,
 * floating, its 1px margin) and regained it as the window advanced. See the `last` variant.
 *
 * - `divider:'inset'` — the same contiguous row model, but the line is INSET 16px from both
 *   row edges, for a table whose separators must align with a neighbouring panel's `p-panel`
 *   content edge rather than run edge-to-edge.
 *
 * `'inset'` is a THIRD VALUE on this one axis rather than a second boolean beside it, because
 * an inset line only means anything when a line is drawn at all — `divider:false` with an inset
 * flag set is a state that cannot render. It swaps the border for an `::after` line because a
 * BORDER CANNOT BE INSET — `border-b` spans
 * the full border box by definition, and the alternatives all cost something the row needs:
 * horizontal padding on the row would move the CELLS too, and a background-gradient line (what
 * the trainer-stats `sx` block used) occupies the row's `background-image`, which the surface
 * layers already own. `::after` is the one free layer — `::before` is the hover/selection tint —
 * and it leaves the tint full-bleed, so the row highlight still spans the whole row while only
 * the separator insets. `after:inset-x-4` is the DS 16px step, matching `p-panel`.
 *
 * The inset line paints `table-row-border` (white @ 4%), NOT the `divider` token (white @ 12%)
 * the full-bleed variant uses. Deliberate, not an oversight: `table-row-border`'s own
 * definition is "subtle 1px row separator on stats/breakdown tables", which is exactly what
 * this variant is for, and at 12% the line reads three times brighter than the ruled panels it
 * is supposed to be indistinguishable from. The full-bleed variant keeps `divider` — it has
 * other consumers and a full-width rule carries different visual weight than an inset one.
 *
 * The inset line is drawn at the row's TOP, not its bottom, and every row draws one. That is
 * the semantic a ruled-rows panel needs and is NOT an arbitrary difference from `divider:true`'s
 * bottom border:
 * - It puts a line between the sticky header band and the FIRST row. The band paints no bottom
 *   border of its own, so a bottom-drawn separator leaves the top edge bare — visibly at odds
 *   with a neighbouring panel whose first body row carries a `border-t`.
 * - There is consequently no trailing line to suppress, so this variant takes no `last` exception
 *   at all: it never approaches the card's bottom edge from above.
 *
 * `after:z-[3]` (= `PINNED_Z_INDEX.rowOverlay`, the same tier the tint `::before` sits in) is
 * LOAD-BEARING wherever the table also pins columns. Unlike `divider:true`'s border — which
 * lives in the row's BORDER box, outside the content box its flex-item cells stretch to, and so
 * cannot be covered — this line is absolutely positioned inside the padding box, directly under
 * the pinned cells' opaque `pinnedCellSurfaceClass` background at `PINNED_Z_INDEX.pinnedBody`
 * (2). Without the lift the separator vanished behind the pinned rail with no other symptom.
 * It must stay BELOW `focus` (4) so the active-cell ring is never crossed by a row line.
 *
 * Sharing the tier with `::before` also REORDERS the two against each other: equal z-index falls
 * back to tree order, so the line now paints ABOVE the tint where it used to sit below. Measured,
 * not assumed. Swapping two source-over layers L and T leaves a residue of exactly
 * `αL·αT·(L − T)` per channel — the base colour cancels out entirely, so the result does not
 * depend on the surface underneath. Here that is `0.04 × 0.08 × 87 = 0.278/255`: below one 8-bit
 * step, and a 3x-DPR screenshot of a hovered row and a selected row on an inset table diffed to
 * ZERO pixels against the pre-lift layering.
 *
 * The binding condition is the PRODUCT `αL·αT ≤ 1/255 ≈ 0.0039`, not either alpha alone — today's
 * 0.0032 already spends 82% of that budget, and halving one alpha buys headroom to double the
 * other. Re-check the product if the `table-row-border` or `action-hover` / selection-gradient
 * alphas move. Note this is a sub-step residue rather than an identity: 0.278 of a step can still
 * cross a per-pixel rounding boundary on some surface, so the zero-pixel diff is an empirical
 * result for the surfaces measured, not a proof for arbitrary ones.
 *
 * - `divider:'ruled'` — `true`'s row model (contiguous, square, a real bottom border, the same
 *   `last` exception) in the `table-rule` hairline instead of `divider`, and the ONLY value the
 *   header band and the summary band also read: `dataTableHeaderVariants` and
 *   `dataTableSummaryBandVariants` each take the same `divider` axis and draw the same token.
 *   This is the model for a WIDE table — 6+ columns, where a row has no guide rail without one.
 *
 * Three things about `'ruled'` that look arbitrary and are not:
 *
 * It is a fourth value rather than a recolour of `true` because `true` already serves a dozen
 * narrower tables, all of which read correctly at `divider`'s 12%. The wide-table rule wants
 * to be quieter than that — it repeats on every one of forty-odd rows — so the two are genuinely
 * different marks, not one mark someone got wrong.
 *
 * The token is OPAQUE (`#1F1F21`) where every other separator here is a white alpha. A ruled
 * table draws the line on the elevation3 header band AND between elevation2 body rows; one
 * alpha over two surfaces gives two different lines, and the seam shows exactly where the eye
 * is following a row. See `tableColors.rule`.
 *
 * It rules ROWS ONLY. Column rules were prototyped and cut on review: the vertical repeat
 * fights the horizontal scan a wide table exists to support, and it costs a descendant
 * selector on every cell's `role` to draw. If they ever come back they are a FIFTH value
 * (`'grid'`), not a flag on this one — the same argument that keeps `'inset'` on this axis.
 *
 * `w-max min-w-full` (not `w-full`) so the row + its state overlay span the FULL column
 * width when columns exceed the viewport, else the overlay seams at the viewport edge.
 *
 * HOVER / SELECTION TINT is a full-row `::before` STATE LAYER, not a row background: a
 * pinned cell carries its own OPAQUE surface, so a row-background tint would sit behind
 * it and the pinned columns would render as un-tinted bands. The overlay sits ABOVE
 * every cell and spans the whole row, so the tint is continuous across pinned + unpinned
 * columns and the selection GRADIENT stays aligned end-to-end (a per-cell background
 * would restart it at every pin boundary). `pointer-events-none` stops it stealing clicks.
 * Its corner rounding tracks the row model (rounded when floating, square when bordered).
 *
 * `before:z-[3]` sits ABOVE the pinned-body layer (2 → pinned columns get the tint) yet
 * BELOW the sticky header (5 → a scrolled row's overlay never paints over the header)
 * and BELOW the focused cell (4 → the active-cell ring stays crisp). Row height is inline
 * `style.height` from `DATA_TABLE_ROW_HEIGHT` — the same constant `estimateSize` reads.
 */
export const dataTableRowVariants = tv({
  base: [
    'relative flex w-max min-w-full',
    // No transition on the hover/selection tint: DataGridPro swapped row backgrounds
    // instantly, and a fade-in on hover reads as input lag on fast pointer sweeps.
    "before:pointer-events-none before:absolute before:inset-0 before:z-[3] before:content-['']",
  ],
  variants: {
    interactive: {
      // Drives only the cursor; the hover TINT applies to every non-selected row via the
      // compound variant below (MUI parity — all rows hover), decoupled from the cursor.
      true: 'cursor-pointer',
      false: '',
    },
    selected: {
      // MUI parity: green primary gradient on the `::before` overlay so it reaches the
      // pinned columns. A selected row shows the gradient ALONE — the hover compound is
      // gated to `selected:false`, so hover never stacks on the selection gradient.
      true: 'before:bg-gradient-primary-horizontal',
      false: '',
    },
    divider: {
      // Floating rounded rows: rounded fill + rounded tint overlay, 1px inter-row gap.
      false: 'mb-[1px] rounded-sm before:rounded-sm',
      // Bordered rows: contiguous, square, a real bottom border — dropped on the LAST row (the
      // `last` axis below) so it never doubles the card's bottom edge.
      true: `border-b ${RULE_COLOR.true}`,
      // Contiguous and square like `true`, but the line is an inset `::after` at the row TOP,
      // drawn on every row and lifted over the pinned rail — see the doc above for all three
      // departures.
      inset:
        "after:pointer-events-none after:absolute after:inset-x-4 after:top-0 after:z-[3] after:h-[1px] after:bg-table-row-border after:content-['']",
      // Contiguous and square like `true`, but in the dimmer OPAQUE `table-rule` hairline,
      // and the header band + summary band draw the same line (the two presets below) so the
      // table reads as one ruled block rather than a bordered body under an unruled header.
      ruled: `border-b ${RULE_COLOR.ruled}`,
    },
    /**
     * Whether this row occupies the body's FINAL position — the one whose trailing separator
     * (or 1px gap) would double the card's bottom edge.
     *
     * Three states, not a boolean, because the DOM answer and the real answer diverge exactly
     * under windowing:
     * - `auto` (default) — fall back to `:last-child`. Correct ONLY when every row of the
     *   dataset is in flow, which is what a hand-composed `TableRow` table (the primitives
     *   stories) is. Keeps that standalone contract intact.
     * - `true` / `false` — the caller knows which slot this is. `DataTable` always passes one
     *   of these, comparing the row's slot against the virtualizer's LAST slot rather than
     *   using DOM position, because a windowed body mounts a moving slice: `:last-child` there
     *   is the bottom of the MOUNTED window, so the exception followed the scroll and the
     *   separator flickered off and on (live in the three windowed divider consumers: admin
     *   features-table, saved-trees, nodelock combos-table). `TablePlaceholderRow` is told the
     *   same way — a placeholder holds a real slot and can be the bottom edge itself.
     */
    last: {
      auto: '',
      true: '',
      false: '',
    },
    // A chrome band (the summary band) is not a data row and never hover-tints —
    // a real variant, not a consumer class-merge override, so the opt-out is visible
    // where the tint is defined.
    hoverTint: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // MUI parity: the hover tint applies to every non-selected row via the overlay, never
    // to a selected row (hover does not stack on the selection gradient).
    { selected: false, hoverTint: true, class: 'hover:before:bg-action-hover' },
    // The trailing exception, per row model. `auto` reproduces the pre-`last` `:last-child`
    // spelling, so an in-flow table is RENDER-identical to before — not byte-identical: these
    // moved out of the `divider` base strings, and `tv` appends compounds after the variant
    // classes, so the emitted token ORDER differs. No tailwind-merge conflict either way.
    { divider: false, last: 'auto', class: 'last:mb-0' },
    { divider: false, last: true, class: 'mb-0' },
    { divider: [true, 'ruled'], last: 'auto', class: 'last:border-b-0' },
    { divider: [true, 'ruled'], last: true, class: 'border-b-0' },
  ],
  defaultVariants: {
    interactive: false,
    selected: false,
    divider: false,
    hoverTint: true,
    last: 'auto',
  },
})

/**
 * A summary band's FACE: its opaque `surface`, the edge that faces the body (`placement`) and
 * that edge's colour (`divider`). One variant set for the band and for its leading overlay, so
 * the overlay cannot disagree with the band it covers. See `dataTableSummaryBandVariants`.
 */
const SUMMARY_BAND_FACE = {
  variants: {
    surface: SURFACE_BG,
    placement: {
      top: 'border-b border-t-0',
      bottom: 'border-b-0 border-t',
    },
    // Colour only — `placement` above already picked which edge faces the body.
    divider: RULE_COLOR,
  },
  defaultVariants: {
    surface: DEFAULT_DATA_TABLE_HEADER_SURFACE,
    placement: 'bottom',
    divider: false,
  },
} as const

/**
 * The PINNED SUMMARY band, layered onto a `TableRow` (which supplies the density height, the
 * `w-max min-w-full` column span and the cell flex track).
 *
 * `sticky` inside the `role="grid"` scroll root is what makes body rows scroll UNDER it while it
 * tracks the columns horizontally — sticky pins only the axis it is offset on, so the band still
 * scrolls sideways in lockstep with the header and the body, and the pinned cells inside it
 * stick to their own edges against the same scrollport. `z-[5]` (= `PINNED_Z_INDEX.summary`)
 * out-ranks the whole body band (0 / 2 / 3 / 4) and stays under the header (6). Being positioned
 * + z-indexed it opens its own stacking context, so its pinned cells' `pinnedBody` z rides above
 * its unpinned cells without escaping the band.
 *
 * The `surface` fill is what makes it OPAQUE — a transparent band would show the rows sliding
 * beneath it.
 *
 * `placement` picks the edge the band pins to, and with it the edge its divider sits on — always
 * the one that faces the body, because that line is what separates the aggregate from the rows:
 * - `bottom` — `border-t` above the band; `border-b-0` neutralises the bordered-row variant's
 *   bottom divider, which `TableRow` would otherwise draw whenever the band is not the scroll
 *   root's last child (the refetch overlay can follow it).
 * - `top` — the bordered-row variant's own `border-b` under the band, and no top line: the
 *   header (or the band above it in a stack) already closes that edge.
 *
 * NO edge class (`top-0` / `bottom-0`). The band's inline `top` / `bottom` offset, `0` included,
 * is the only thing that places it. The Vue shell hosts these tables and loads Vuetify's
 * utility sheet on every page, where `.top-0` and `.bottom-0` set the edge with `!important`.
 * An `!important` rule beats an inline style, so a band with that class pinned to the
 * scrollport edge whatever its offset: a top stack slid UNDER the header, and a bottom stack
 * collapsed onto one line.
 */
export const dataTableSummaryBandVariants = tv({ base: 'sticky z-[5]', ...SUMMARY_BAND_FACE })

/**
 * A summary band's LEADING OVERLAY (`summaryRow.leading`): the band's face again, on the CARD
 * and outside the horizontal scroll. Opaque for the band's own reason, one level up — the band's
 * leading cells still render under it, and a column that aggregates to a value can be dragged
 * there. `z-[6]` puts it over the band (`z-[5]`).
 */
export const dataTableSummaryLeadingVariants = tv({
  base: 'pointer-events-none absolute left-0 z-[6]',
  ...SUMMARY_BAND_FACE,
})

/**
 * Body cell. The active-cell ring (U6) is keyed off the `data-[active]` ATTRIBUTE, not
 * the cell's own `:focus`: in the `aria-activedescendant` model real DOM focus stays on
 * the grid container and never reaches the cell, so a `:focus` ring would never render.
 *
 * The ring is additionally GATED on the container's `:focus-visible`
 * (`group-focus-visible/grid:`, the marker on `dataTableRootVariants`), so it paints
 * ONLY for keyboard focus: the container is the single `tabIndex=0` tab-stop, and
 * without the gate, clicking a non-focusable descendant (a sort header, a body cell)
 * fired the `onFocus` anchor and painted the ring on the first cell after a mouse-driven
 * sort. Only the ring's PAINT is gated — `data-active` / `aria-activedescendant` are
 * unchanged, so the a11y model stays intact.
 *
 * `relative` on the active cell is LOAD-BEARING: it opens the positioning context that
 * makes the `z-index` lift over a sticky pinned column (C-397 / `PINNED_Z_INDEX.focus`)
 * take effect instead of being a no-op on a `position: static` cell. Gated with the ring,
 * so the lift applies exactly when the ring is visible.
 *
 * CONTENT CONTRACT — this cell IS a centred flex row (`items-center`, plus `justify-*` from
 * `meta.align`), so a `cell:` renderer's children are laid out and vertically centred already:
 * return a FRAGMENT for the ordinary horizontal case. Re-wrapping in a plain `flex` row is
 * redundant AND reverts to `align-items: stretch` — with mixed-height children (a 24px
 * `IconButton` beside 20px `body2` text) the taller child sets the line height, the text's line
 * box stretches with it and paints at the TOP of that box, so the label lands ~2px above centre.
 * Density-independent, and jsdom computes no layout, so only a browser or Chromatic catches it.
 * Wrap only for a genuinely DIFFERENT layout — and then state that wrapper's cross-axis
 * explicitly. NOT fixable from here: forcing alignment onto children would collapse the
 * deliberate `flex-col` stretch in `summary-table-view` (its bar would go 0px wide) and the
 * `self-stretch` fill in `blockers-table-view`.
 */
export const dataTableCellVariants = tv({
  base: [
    // MUI parity: the body cell renders the shared `body2` token (measured MUI DataGrid
    // cell typography); `tabular-nums` keeps numeric columns digit-aligned.
    typography.body2,
    // `whitespace-nowrap` (MUI DataGrid parity — `.MuiDataGrid-cell` never wraps): the row
    // height is a fixed per-density px the virtualizer's `estimateSize` reads, so a cell that
    // wrapped to a second line would overflow its slot (a 2-line `body2` is 40px in the 30px
    // compact row). Combined with `overflow-hidden`, over-wide content clips to one line.
    'flex min-w-0 shrink-0 items-center overflow-hidden whitespace-nowrap px-3 text-text-primary tabular-nums',
    // `relative` opens the positioning context the C-397 z-lift needs (a no-op on
    // `position: static`); `z-[4]` matches `PINNED_Z_INDEX.focus` (no `4` token in the
    // z-scale). The `group-focus-visible/grid:` prefix gates the block on keyboard focus
    // (rationale above).
    'group-focus-visible/grid:data-[active]:relative group-focus-visible/grid:data-[active]:z-[4] group-focus-visible/grid:data-[active]:rounded-sm group-focus-visible/grid:data-[active]:ring-2 group-focus-visible/grid:data-[active]:ring-inset group-focus-visible/grid:data-[active]:ring-primary-main',
  ],
  variants: {
    align: {
      left: 'justify-start text-left',
      center: 'justify-center text-center',
      right: 'justify-end text-right',
    },
  },
  defaultVariants: {
    align: 'left',
  },
})
